import type { GridProps } from '@mui/material/Grid';

type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type LegacyGridBreakpointProps = {
  item?: boolean;
  xs?: boolean | number;
  sm?: boolean | number;
  md?: boolean | number;
  lg?: boolean | number;
  xl?: boolean | number;
  zeroMinWidth?: boolean;
};

export type LegacyGridProps = GridProps & LegacyGridBreakpointProps;

const breakpointKeys: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl'];

/** MUI 9에서 Grid DOM으로 새지 않도록 system prop을 sx로 이동 */
export const gridSystemPropKeys = [
  'justifyContent',
  'alignItems',
  'flexDirection',
  'flexWrap',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'display',
  'gap',
  'columnGap',
  'rowGap',
  'm',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',
  'p',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'order',
  'alignSelf',
  'justifySelf',
] as const;

type GridSystemPropKey = (typeof gridSystemPropKeys)[number];

/** MUI 5 GridLegacy container prop → MUI 9 flex system prop */
const legacyFlexPropAliases: Record<string, GridSystemPropKey> = {
  direction: 'flexDirection',
  wrap: 'flexWrap',
};

const gridColumnCount = 12;

const isLegacyFractionalColumn = (
  value: boolean | number | undefined,
): value is number =>
  typeof value === 'number' && !Number.isInteger(value);

const hasIntegerLegacyBreakpoint = (
  props: LegacyGridBreakpointProps,
): boolean =>
  breakpointKeys.some((breakpoint) => {
    const value = props[breakpoint];
    return (
      value !== undefined &&
      value !== false &&
      !isLegacyFractionalColumn(value)
    );
  });

const hasFullWidthIntegerBreakpoint = (
  props: LegacyGridBreakpointProps,
): boolean =>
  breakpointKeys.some((breakpoint) => {
    const value = props[breakpoint];
    return (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      value === gridColumnCount
    );
  });

/**
 * xs=2 sm=1.025 등 정수·소수 breakpoint가 같은 행 분할용으로 섞인 item.
 * xs=12 md=1.75(전체 너비 + 일부 구간만 소수) 패턴과 구분한다.
 */
const usesMixedSplitRowBreakpoints = (
  props: LegacyGridBreakpointProps,
): boolean => {
  const hasFractional = breakpointKeys.some((breakpoint) =>
    isLegacyFractionalColumn(props[breakpoint]),
  );
  if (!hasFractional || !hasIntegerLegacyBreakpoint(props)) {
    return false;
  }
  return !hasFullWidthIntegerBreakpoint(props);
};

/** 정수·소수 혼합 split-row item — breakpoint별 flex ratio */
const buildLegacyFlexRatioSxForNumericBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const flexByBreakpoint: Partial<Record<BreakpointKey, string>> = {};

  breakpointKeys.forEach((breakpoint) => {
    const value = props[breakpoint];
    if (typeof value === 'number') {
      flexByBreakpoint[breakpoint] = `${value} 1 0`;
    }
  });

  const flexEntries = Object.entries(flexByBreakpoint);
  if (flexEntries.length === 0) {
    return undefined;
  }

  if (flexEntries.length === 1) {
    return {
      flex: flexEntries[0][1],
      minWidth: 0,
      maxWidth: '100%',
    };
  }

  return {
    flex: flexByBreakpoint,
    minWidth: 0,
    maxWidth: '100%',
  };
};

/** GridLegacy item column 수(·소수) → % 너비 */
export const legacyBreakpointToWidthPercent = (
  value: boolean | number | undefined,
): string | undefined => {
  if (value === undefined || value === false || value === true) {
    return undefined;
  }

  return `${(value / gridColumnCount) * 100}%`;
};

/**
 * MUI 5 GridLegacy는 container+item 단독 사용 시에도 breakpoint 너비 클래스를 적용했으나,
 * MUI 9 Grid는 부모 container 없이 size만으로는 너비가 줄지 않는다.
 * Account 설정 폼(sm=8, xl=6) 등 레거시 패턴 복원용.
 */
export const buildLegacyStandaloneItemWidthSx = (
  props: LegacyGridBreakpointProps & {
    item?: boolean;
    container?: boolean;
  },
) => {
  if (!props.item || !props.container) {
    return undefined;
  }

  const widthByBreakpoint: Partial<Record<BreakpointKey, string>> = {};

  breakpointKeys.forEach((breakpoint) => {
    const percent = legacyBreakpointToWidthPercent(props[breakpoint]);
    if (percent) {
      widthByBreakpoint[breakpoint] = percent;
    }
  });

  if (Object.keys(widthByBreakpoint).length === 0) {
    return {
      width: '100%',
      maxWidth: '100%',
    };
  }

  if (Object.keys(widthByBreakpoint).length === 1) {
    const singleWidth = Object.values(widthByBreakpoint)[0];
    return {
      width: singleWidth,
      maxWidth: singleWidth,
      flexBasis: singleWidth,
      flexGrow: 0,
    };
  }

  return {
    width: widthByBreakpoint,
    maxWidth: widthByBreakpoint,
    flexBasis: widthByBreakpoint,
    flexGrow: 0,
  };
};

/**
 * MUI 9 Grid size만으로는 형제 container+item 행에서 xs={3} 등 정수 item 너비가 prod와 어긋날 수 있다.
 * 단일 정수 breakpoint item(예: CardBoard log xs=3)에 % width·maxWidth를 명시한다.
 */
/** xs+sm만 있는 표준 2열(item xs=12 sm=6 등)은 MUI 9 Grid size가 gap을 반영하므로 % width sx를 쓰지 않는다 */
const usesStandardTwoColumnIntegerBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 2) {
    return false;
  }

  const [firstBreakpoint, secondBreakpoint] = definedBreakpoints;
  return (
    firstBreakpoint === 'xs' &&
    secondBreakpoint === 'sm' &&
    typeof props.xs === 'number' &&
    Number.isInteger(props.xs) &&
    typeof props.sm === 'number' &&
    Number.isInteger(props.sm)
  );
};

/** Router CommSetting 모달 라벨·필드(item xs=4 / xs=8 단독) — gap 포함 너비는 MUI 9 size에 맡긴다 */
const usesCommSettingModalIntegerBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 1 || definedBreakpoints[0] !== 'xs') {
    return false;
  }

  return props.xs === 4 || props.xs === 8;
};

/** MES Dashboard KPI 등 4열(item xs=12 sm=6 md=3) — gap 포함 너비는 MUI 9 size에 맡긴다 */
const usesFourColumnKpiGridBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 3) {
    return false;
  }

  return props.xs === 12 && props.sm === 6 && props.md === 3;
};

/** MES Dashboard 주문·생산 현황 등 2열(item xs=12 md=6) — gap 포함 너비는 MUI 9 size에 맡긴다 */
const usesTwoColumnMdGridBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 2) {
    return false;
  }

  return props.xs === 12 && props.md === 6;
};

/** LlmAgent 할 일·시스템 프롬프트 2열(item xs=12 xl=6) — gap 포함 너비는 MUI 9 size에 맡긴다 */
const usesTwoColumnXlGridBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 2) {
    return false;
  }

  return props.xs === 12 && props.xl === 6;
};

/** FieldControl/DataControlCard 그리드(xs=12 sm=6 md=6 lg=4 xl=2) — gap 포함 너비는 MUI 9 size에 맡긴다 */
const usesFieldControlCardGridBreakpoints = (
  props: LegacyGridBreakpointProps,
) => {
  const definedBreakpoints = breakpointKeys.filter(
    (breakpoint) => props[breakpoint] !== undefined,
  );
  if (definedBreakpoints.length !== 5) {
    return false;
  }

  return (
    props.xs === 12 &&
    props.sm === 6 &&
    props.md === 6 &&
    props.lg === 4 &&
    typeof props.xl === 'number' &&
    Number.isInteger(props.xl)
  );
};

export const buildLegacyIntegerItemWidthSx = (
  props: LegacyGridBreakpointProps & {
    item?: boolean;
    container?: boolean;
  },
) => {
  if (!props.item || props.container) {
    return undefined;
  }

  if (usesStandardTwoColumnIntegerBreakpoints(props)) {
    return undefined;
  }

  if (usesFourColumnKpiGridBreakpoints(props)) {
    return undefined;
  }

  if (usesTwoColumnMdGridBreakpoints(props)) {
    return undefined;
  }

  if (usesTwoColumnXlGridBreakpoints(props)) {
    return undefined;
  }

  if (usesCommSettingModalIntegerBreakpoints(props)) {
    return undefined;
  }

  if (usesFieldControlCardGridBreakpoints(props)) {
    return undefined;
  }

  const hasFractionalBreakpoint = breakpointKeys.some((breakpoint) =>
    isLegacyFractionalColumn(props[breakpoint]),
  );
  if (hasFractionalBreakpoint) {
    return undefined;
  }

  const widthByBreakpoint: Partial<Record<BreakpointKey, string>> = {};

  breakpointKeys.forEach((breakpoint) => {
    const value = props[breakpoint];
    if (typeof value === 'number' && Number.isInteger(value)) {
      const percent = legacyBreakpointToWidthPercent(value);
      if (percent) {
        widthByBreakpoint[breakpoint] = percent;
      }
    }
  });

  const breakpointCount = Object.keys(widthByBreakpoint).length;
  if (breakpointCount === 0) {
    return undefined;
  }

  if (breakpointCount === 1) {
    const singleWidth = Object.values(widthByBreakpoint)[0];
    return {
      width: singleWidth,
      maxWidth: singleWidth,
      flexBasis: singleWidth,
      flexGrow: 0,
    };
  }

  // EditData 라벨·필드(xs=12, lg=4, xl=3) 등 복수 정수 breakpoint item
  return {
    width: widthByBreakpoint,
    maxWidth: widthByBreakpoint,
    flexBasis: widthByBreakpoint,
    flexGrow: 0,
  };
};

/**
 * MUI 9 Grid size는 정수 1–12만 허용. xs={10.75}·md={1.75} 등 소수 breakpoint는 % width sx로 복원.
 */
export const buildLegacyFractionalItemWidthSx = (
  props: LegacyGridBreakpointProps & { item?: boolean },
) => {
  if (!props.item) {
    return undefined;
  }

  if (usesMixedSplitRowBreakpoints(props)) {
    return buildLegacyFlexRatioSxForNumericBreakpoints(props);
  }

  const widthByBreakpoint: Partial<Record<BreakpointKey, string>> = {};

  breakpointKeys.forEach((breakpoint) => {
    const value = props[breakpoint];
    if (isLegacyFractionalColumn(value)) {
      const percent = legacyBreakpointToWidthPercent(value);
      if (percent) {
        widthByBreakpoint[breakpoint] = percent;
      }
    }
  });

  if (Object.keys(widthByBreakpoint).length === 0) {
    return undefined;
  }

  // 소수 breakpoint만(9.75+2.25 등) — % width + columnGap가 100%를 넘어 wrap되므로 flex ratio로 복원
  if (!hasIntegerLegacyBreakpoint(props)) {
    const flexByBreakpoint: Partial<Record<BreakpointKey, string>> = {};
    breakpointKeys.forEach((breakpoint) => {
      const value = props[breakpoint];
      if (isLegacyFractionalColumn(value)) {
        flexByBreakpoint[breakpoint] = `${value} 1 0`;
      }
    });

    const flexEntries = Object.entries(flexByBreakpoint);
    if (flexEntries.length === 1) {
      return {
        flex: flexEntries[0][1],
        minWidth: 0,
        maxWidth: '100%',
      };
    }

    if (flexEntries.length > 1) {
      return {
        flex: flexByBreakpoint,
        minWidth: 0,
        maxWidth: '100%',
      };
    }
  }

  if (Object.keys(widthByBreakpoint).length === 1) {
    const [singleBreakpoint] = Object.entries(widthByBreakpoint)[0] as [
      BreakpointKey,
      string,
    ];
    const fractionalColumnValue = props[singleBreakpoint];
    // 정수 breakpoint와 혼합(예: xs=12 … xl=3.5)이면 해당 구간에서만 % width 적용
    if (hasIntegerLegacyBreakpoint(props)) {
      const singleWidth = widthByBreakpoint[singleBreakpoint];
      return {
        width: { [singleBreakpoint]: singleWidth },
        maxWidth: '100%',
        flexBasis: { [singleBreakpoint]: singleWidth },
        flexGrow: 0,
      };
    }

    const singleWidth = widthByBreakpoint[singleBreakpoint];
    return {
      width: singleWidth,
      maxWidth: '100%',
      flexBasis: singleWidth,
      flexGrow: 0,
    };
  }

  return {
    width: widthByBreakpoint,
    maxWidth: '100%',
    flexBasis: widthByBreakpoint,
    flexGrow: 0,
  };
};

export const mapLegacyGridSize = (
  props: LegacyGridBreakpointProps &
    Pick<GridProps, 'size'> & {
      /** MUI 5 GridLegacy item — breakpoint 없을 때 size 미지정 */
      item?: boolean;
      container?: boolean;
    },
) => {
  if (props.size !== undefined) {
    return props.size;
  }

  const sizeByBreakpoint: Partial<Record<BreakpointKey, number | string | false>> = {};
  let hasLegacyBreakpoint = false;
  const useFlexRatioForAllNumericBreakpoints =
    usesMixedSplitRowBreakpoints(props);

  breakpointKeys.forEach((breakpoint) => {
    const value = props[breakpoint];
    if (value === undefined) {
      return;
    }

    hasLegacyBreakpoint = true;
    if (value === false) {
      sizeByBreakpoint[breakpoint] = false;
      return;
    }

    if (value === true) {
      sizeByBreakpoint[breakpoint] = 'grow';
      return;
    }

    if (
      useFlexRatioForAllNumericBreakpoints ||
      isLegacyFractionalColumn(value)
    ) {
      // lg=2.4·xs=2 sm=1.025 등 — sx flex/width로 처리; cascade full-width 방지
      sizeByBreakpoint[breakpoint] = 'auto';
      return;
    }

    sizeByBreakpoint[breakpoint] = value;
  });

  if (!hasLegacyBreakpoint) {
    // MUI 9 Grid는 size 없으면 content 너비만 차지 → Dashboard 등 item container 래퍼가 반쪽으로 줄어듦
    if (props.item && props.container) {
      return 12;
    }
    // MUI 5 GridLegacy `<Grid item>` 단독 — grow로 flex 행 안에서 공간 분배
    if (props.item && !props.container) {
      return 'grow';
    }
    return undefined;
  }

  const entries = Object.entries(sizeByBreakpoint);
  if (entries.length === 0) {
    return undefined;
  }

  if (entries.length === 1) {
    return entries[0][1];
  }

  return sizeByBreakpoint;
};

export const splitLegacyGridProps = (props: LegacyGridProps) => {
  const hasLegacyBreakpoint =
    props.xs !== undefined ||
    props.sm !== undefined ||
    props.md !== undefined ||
    props.lg !== undefined ||
    props.xl !== undefined ||
    props.size !== undefined;

  /** LineWrapper·FormLineWrapper 등 container-only 행 — MUI 9에서 부모 Grid 직계 자식 너비 0 방지 */
  const shouldDefaultFullWidthRow =
    props.container && !props.item && !hasLegacyBreakpoint;

  const normalizedProps = shouldDefaultFullWidthRow
    ? { ...props, item: true, xs: 12 }
    : props;

  const propsWithFlexAliases = { ...normalizedProps } as LegacyGridProps &
    Record<string, unknown>;
  Object.entries(legacyFlexPropAliases).forEach(
    ([legacyProp, systemProp]) => {
      if (
        legacyProp in propsWithFlexAliases &&
        !(systemProp in propsWithFlexAliases)
      ) {
        propsWithFlexAliases[systemProp] = propsWithFlexAliases[legacyProp];
      }
      delete propsWithFlexAliases[legacyProp];
    },
  );

  const {
    item,
    container,
    xs,
    sm,
    md,
    lg,
    xl,
    zeroMinWidth: _zeroMinWidth,
    size,
    sx,
    ...rest
  } = propsWithFlexAliases;

  const systemSx: Partial<Record<GridSystemPropKey, unknown>> = {};
  // container는 MUI Grid에 그대로 전달해야 flex 컨테이너로 동작 (item은 size로 변환했으므로 제외)
  const forwardedProps: Record<string, unknown> = { ...rest };
  if (container) {
    forwardedProps.container = true;
  }

  gridSystemPropKeys.forEach((systemPropKey) => {
    if (systemPropKey in forwardedProps) {
      systemSx[systemPropKey] = forwardedProps[systemPropKey];
      delete forwardedProps[systemPropKey];
    }
  });

  const resolvedSize = mapLegacyGridSize({
    xs,
    sm,
    md,
    lg,
    xl,
    size,
    item,
    container,
  });
  const legacyStandaloneItemWidthSx = buildLegacyStandaloneItemWidthSx({
    item,
    container,
    xs,
    sm,
    md,
    lg,
    xl,
  });
  const legacyFractionalItemWidthSx = buildLegacyFractionalItemWidthSx({
    item,
    xs,
    sm,
    md,
    lg,
    xl,
  });
  const legacyIntegerItemWidthSx = buildLegacyIntegerItemWidthSx({
    item,
    container,
    xs,
    sm,
    md,
    lg,
    xl,
  });
  const mergedSx =
    legacyStandaloneItemWidthSx ||
    legacyFractionalItemWidthSx ||
    legacyIntegerItemWidthSx ||
    Object.keys(systemSx).length > 0 ||
    sx
      ? {
          ...legacyStandaloneItemWidthSx,
          ...legacyFractionalItemWidthSx,
          ...legacyIntegerItemWidthSx,
          ...systemSx,
          ...(sx as object | undefined),
        }
      : sx;

  return {
    forwardedProps,
    resolvedSize,
    mergedSx,
  };
};
